import * as React from "react"
import { UsersIcon } from "lucide-react"

import { SelectSinBorde } from "@/components/ui/select-sin-borde"
import { cn } from "@/lib/utils"

interface OpcionDelSelector {
  valor: string
  etiqueta: string
}

/**
 * Filtro compuesto PERSONA + ROL: una píldora con borde, el ícono de personas y
 * dos selectores sin caja separados por una línea ("Todas las personas │ en
 * cualquier rol"). Se lee como una frase: "<quién> <en qué papel>".
 *
 * Con una persona elegida la píldora se marca (borde de tinta y fondo suave) para
 * que se vea de un vistazo que la lista está filtrada. Sin persona el rol queda
 * apagado: el servidor lo ignora, y un control que no hace nada engaña.
 *
 * `personas` NO incluye "Todas las personas": ese es el valor vacío y lo pone
 * este componente. El valor del rol por defecto lo decide quien lo usa.
 */
function FiltroDePersona({
  personas,
  persona,
  onPersona,
  roles,
  rol,
  onRol,
  className,
}: {
  personas: OpcionDelSelector[]
  /** `""` = todas. */
  persona: string
  onPersona: (valor: string) => void
  roles: OpcionDelSelector[]
  rol: string
  onRol: (valor: string) => void
  className?: string
}) {
  const activo = persona !== ""

  return (
    <div
      data-slot="filtro-de-persona"
      data-activo={activo}
      className={cn(
        "flex h-[2.375rem] items-center gap-1.5 rounded-[0.75rem] border-[1.5px] pr-1.5 pl-3 transition-colors",
        activo ? "border-ink bg-divisor" : "border-input bg-card",
        className
      )}
    >
      <UsersIcon aria-hidden className="text-ink-medio size-3.5 shrink-0" />
      <SelectSinBorde
        aria-label="Persona"
        value={persona}
        onChange={(evento) => onPersona(evento.target.value)}
      >
        <option value="">Todas las personas</option>
        {personas.map((opcion) => (
          <option key={opcion.valor} value={opcion.valor}>
            {opcion.etiqueta}
          </option>
        ))}
      </SelectSinBorde>
      <span aria-hidden className="bg-input h-[1.125rem] w-px" />
      <SelectSinBorde
        aria-label="Rol"
        value={rol}
        disabled={!activo}
        title={activo ? undefined : "Elige una persona para filtrar por su rol."}
        onChange={(evento) => onRol(evento.target.value)}
      >
        {roles.map((opcion) => (
          <option key={opcion.valor} value={opcion.valor}>
            {opcion.etiqueta}
          </option>
        ))}
      </SelectSinBorde>
    </div>
  )
}

export { FiltroDePersona }
export type { OpcionDelSelector }
